import React, { useState, useRef, useCallback } from 'react';
import { Box, TextField, IconButton, Tooltip } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import TassQuickActions from './TassQuickActions';
import SpeechToText from '../../SpeechToText';

function TassInputBar({ onSend, disabled }) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || disabled) return;
    onSend(text);
    setInputValue('');
    // Refocus input after sending
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [inputValue, disabled, onSend]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleQuickAction = useCallback((text) => {
    if (disabled) return;
    onSend(text);
  }, [disabled, onSend]);

  return (
    <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      {/* Quick Actions */}
      <TassQuickActions onAction={handleQuickAction} disabled={disabled} />

      {/* Input row */}
      <Box display="flex" alignItems="flex-end" gap={0.75} sx={{ mt: 0.75 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={3}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Waiting for response...' : 'Ask TASS...'}
          disabled={disabled}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.85rem'
            }
          }}
        />
        <SpeechToText
          onTranscript={(transcript) => {
            const newValue = inputValue ? `${inputValue} ${transcript}` : transcript;
            setInputValue(newValue);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          continuous={false}
          showInterimText={false}
        />
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim() || disabled}
              color="primary"
              size="small"
              aria-label="Send message"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 34,
                height: 34,
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default TassInputBar;
